// Silent offline queue for driver delivery actions. Scope: routes/stops always
// load online; ONLY the delivery submission itself (mark Delivered w/ 2 photos,
// or Not Delivered w/ reason) is queued in IndexedDB when the network call
// fails, then retried automatically on the next 'online' event / poll tick —
// no UI, no toast, no "syncing" indicator, per explicit product requirement.
import { apiClient } from "./apiClient";

const DB_NAME = "vt_driver_sync";
const STORE = "pending_actions";
const ROUTE_STORE = "route_cache";
let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(ROUTE_STORE)) {
        db.createObjectStore(ROUTE_STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function putItem(item) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteItem(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getAllItems() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Offline route cache. When a driver loads a route (or their "today" list)
// online, we snapshot it to IndexedDB so the same screen still works with no
// / bad signal (addresses, phones, coords, stop order all available offline).
// ---------------------------------------------------------------------------
async function cachePut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ROUTE_STORE, "readwrite");
    tx.objectStore(ROUTE_STORE).put({ key, value, cachedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function cacheGet(key) {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(ROUTE_STORE, "readonly");
    const req = tx.objectStore(ROUTE_STORE).get(key);
    req.onsuccess = () => resolve(req.result ? req.result.value : null);
    req.onerror = () => resolve(null);
  });
}

export const cacheRoute = (routeId, data) => cachePut(`route:${routeId}`, data);
export const getCachedRoute = (routeId) => cacheGet(`route:${routeId}`);
export const cacheToday = (data) => cachePut("today", data);
export const getCachedToday = () => cacheGet("today");

// In-memory + localStorage optimistic status overrides so the UI can reflect
// "Delivered"/"Not Delivered" immediately, even while the real submission is
// still queued offline. Keyed by delivery id.
const OVERRIDE_KEY = "vt_driver_status_overrides";
function readOverrides() {
  try {
    return JSON.parse(localStorage.getItem(OVERRIDE_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeOverrides(map) {
  localStorage.setItem(OVERRIDE_KEY, JSON.stringify(map));
}

export function getLocalOverride(deliveryId) {
  return readOverrides()[String(deliveryId)] || null;
}

// Count of delivery actions still waiting to sync (shown as a tiny "uploading…"
// indicator so the driver has confidence their offline work is queued).
export function getPendingCount() {
  const map = readOverrides();
  const cash = readCashOverrides();
  return Object.values(map).filter((o) => o && o.queued).length
    + Object.values(cash).filter((o) => o && o.queued).length;
}

// Optimistic cash-collection overrides, kept parallel to the delivery-status
// overrides so collecting cash never clobbers a Delivered/Not-Delivered state.
const CASH_OVERRIDE_KEY = "vt_driver_cash_overrides";
function readCashOverrides() {
  try {
    return JSON.parse(localStorage.getItem(CASH_OVERRIDE_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeCashOverrides(map) {
  localStorage.setItem(CASH_OVERRIDE_KEY, JSON.stringify(map));
}
export function getCashOverride(deliveryId) {
  return readCashOverrides()[String(deliveryId)] || null;
}
function setCashOverride(deliveryId, override) {
  const map = readCashOverrides();
  map[String(deliveryId)] = override;
  writeCashOverrides(map);
  notifySubscribers();
}
function clearCashOverride(deliveryId) {
  const map = readCashOverrides();
  delete map[String(deliveryId)];
  writeCashOverrides(map);
  notifySubscribers();
}

function setLocalOverride(deliveryId, override) {
  const map = readOverrides();
  map[String(deliveryId)] = override;
  writeOverrides(map);
  notifySubscribers();
}

function clearLocalOverride(deliveryId) {
  const map = readOverrides();
  delete map[String(deliveryId)];
  writeOverrides(map);
  notifySubscribers();
}

const subscribers = new Set();
export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}
function notifySubscribers() {
  subscribers.forEach((cb) => {
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}

async function submitDeliver(deliveryId, photo1Blob, photo2Blob) {
  const form = new FormData();
  form.append("photo1", photo1Blob, "photo1.jpg");
  form.append("photo2", photo2Blob, "photo2.jpg");
  const { data } = await apiClient.post(`/driver/deliveries/${deliveryId}/deliver`, form);
  return data;
}

async function submitNotDelivered(deliveryId, reason) {
  const { data } = await apiClient.post(`/driver/deliveries/${deliveryId}/not-delivered`, { reason });
  return data;
}

async function submitCollectCash(deliveryId, amount) {
  const { data } = await apiClient.post(`/driver/deliveries/${deliveryId}/collect-cash`, { amount });
  return data;
}

export async function queueDeliverAction(deliveryId, photo1Blob, photo2Blob) {
  setLocalOverride(deliveryId, { delivery_status: 2, queued: true });
  try {
    await submitDeliver(deliveryId, photo1Blob, photo2Blob);
    clearLocalOverride(deliveryId);
    return { queued: false };
  } catch (error) {
    if (isNetworkError(error)) {
      await putItem({
        key: `deliver:${deliveryId}:${Date.now()}`,
        type: "deliver",
        deliveryId,
        photo1Blob,
        photo2Blob,
        createdAt: Date.now(),
      });
      return { queued: true };
    }
    clearLocalOverride(deliveryId);
    throw error;
  }
}

export async function queueNotDeliveredAction(deliveryId, reason) {
  setLocalOverride(deliveryId, { delivery_status: 3, not_delivered_reason: reason, queued: true });
  try {
    await submitNotDelivered(deliveryId, reason);
    clearLocalOverride(deliveryId);
    return { queued: false };
  } catch (error) {
    if (isNetworkError(error)) {
      await putItem({
        key: `notdelivered:${deliveryId}:${Date.now()}`,
        type: "not-delivered",
        deliveryId,
        reason,
        createdAt: Date.now(),
      });
      return { queued: true };
    }
    clearLocalOverride(deliveryId);
    throw error;
  }
}

export async function queueCollectCash(deliveryId, amount) {
  setCashOverride(deliveryId, { collected: true, amount, queued: true });
  try {
    await submitCollectCash(deliveryId, amount);
    setCashOverride(deliveryId, { collected: true, amount, queued: false });
    return { queued: false };
  } catch (error) {
    if (isNetworkError(error)) {
      await putItem({
        key: `collectcash:${deliveryId}:${Date.now()}`,
        type: "collect-cash",
        deliveryId,
        amount,
        createdAt: Date.now(),
      });
      return { queued: true };
    }
    clearCashOverride(deliveryId);
    throw error;
  }
}

function isNetworkError(error) {
  return !error?.response;
}

let flushing = false;
export async function flushQueue() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const items = await getAllItems();
    for (const item of items) {
      try {
        if (item.type === "deliver") {
          await submitDeliver(item.deliveryId, item.photo1Blob, item.photo2Blob);
        } else if (item.type === "collect-cash") {
          await submitCollectCash(item.deliveryId, item.amount);
          setCashOverride(item.deliveryId, { collected: true, amount: item.amount, queued: false });
          await deleteItem(item.key);
          continue;
        } else {
          await submitNotDelivered(item.deliveryId, item.reason);
        }
        await deleteItem(item.key);
        clearLocalOverride(item.deliveryId);
      } catch (error) {
        if (!isNetworkError(error)) {
          // Server rejected it (e.g. already delivered) — drop it, don't retry forever.
          await deleteItem(item.key);
          if (item.type === "collect-cash") clearCashOverride(item.deliveryId);
          else clearLocalOverride(item.deliveryId);
        }
        // else: still offline, keep it queued and try again next tick.
      }
    }
  } finally {
    flushing = false;
  }
}

let syncLoopStarted = false;
export function startSyncLoop() {
  if (syncLoopStarted) return () => {};
  syncLoopStarted = true;
  window.addEventListener("online", flushQueue);
  const interval = setInterval(flushQueue, 15000);
  flushQueue();
  return () => {
    window.removeEventListener("online", flushQueue);
    clearInterval(interval);
    syncLoopStarted = false;
  };
}
